import { useRef } from "react";

import { SentenceInput } from "./SentenceInput";

/**
 * The phone's way in (`6c`).
 *
 * There is no tray agent on a phone — no global hotkey, no screen capture, no deep link. Text
 * arrives by paste, so the mobile Read surface needs an input that is always there, sitting above
 * the log with the `部` button beside it. The desktop's equivalent is the chip at the foot of the
 * log, which opens the same field on click; both share `SentenceInput`.
 *
 * **撮 is the other way in** (日本-711): a photo of the Switch screen, straightened and OCR'd on the
 * phone. It opens the rear CAMERA directly (`capture="environment"`) — Alex's call after testing,
 * because the chooser put a gallery tap in front of every capture. A photo already taken comes in
 * through the capture layer's ライブラリから選ぶ.
 */
export function ComposeRow({
  onSubmit,
  onRadicals,
  radicalsOpen,
  onPhoto,
  photoOpen,
}: {
  onSubmit: (text: string) => void;
  onRadicals: () => void;
  radicalsOpen: boolean;
  onPhoto: (file: File) => void;
  photoOpen: boolean;
}) {
  const picker = useRef<HTMLInputElement>(null);
  return (
    <div className="flex shrink-0 items-center gap-2 border-b border-line bg-panel px-4 pb-[10px] pt-0.5">
      <SentenceInput
        onSubmit={onSubmit}
        placeholder="日本語を貼り付けるか入力…"
        enterKeyHint="go"
        className="h-9 min-w-0 flex-1 rounded-[var(--r-3)] border border-line-strong bg-void px-[11px] font-jp text-[16px]/[1] text-ink outline-none placeholder:text-ink-3 focus:border-accent"
      />
      <button
        type="button"
        data-check="read.photo"
        onClick={() => picker.current?.click()}
        aria-pressed={photoOpen}
        aria-label="写真から読み取る"
        className={[
          "flex h-9 w-9 shrink-0 items-center justify-center rounded-[var(--r-3)] border font-jp text-[18px]/[1]",
          photoOpen
            ? "border-accent bg-accent-tint text-[color:var(--accent-text)]"
            : "border-line-strong bg-panel text-ink-2",
        ].join(" ")}
      >
        撮
      </button>
      <input
        ref={picker}
        data-check="read.photo.input"
        type="file"
        accept="image/*"
        capture="environment"
        hidden
        onChange={(event) => {
          const file = event.target.files?.[0];
          // Cleared, so choosing the same photo twice still fires a change.
          event.target.value = "";
          if (file) onPhoto(file);
        }}
      />
      <button
        type="button"
        onClick={onRadicals}
        aria-pressed={radicalsOpen}
        aria-label="部品検索"
        className={[
          "flex h-9 w-9 shrink-0 items-center justify-center rounded-[var(--r-3)] border font-jp text-[18px]/[1]",
          // `--accent-text` on the tint, never `--accent-tint` on it: the old value was
          // `--action-darkest`, which the compatibility block maps to `--accent-tint`, so the
          // pressed state painted 部 in its own background and the button went blank.
          radicalsOpen
            ? "border-accent bg-accent-tint text-[color:var(--accent-text)]"
            : "border-line-strong bg-panel text-ink-2",
        ].join(" ")}
      >
        部
      </button>
    </div>
  );
}
